import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, IconButton, Pagination, Stack, Box, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Chip, TextField } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { deleteUserRequest, getRequestUserList, resetUserDelete, sendResetEmailRequest, clearSendResetEmail, setUserStatusRequest, clearSetUserStatus } from '../../../redux/modules/staff/slice';
import { DeleteConfirmationModal } from '../../../utils/modal';
import SearchInput from '../../../utils/searchInput';
import { getRolesRequest } from '../../../redux/modules/permissions/slice';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
const EllipsisText = ({ children }) => (
  <Typography>
    {children}
  </Typography>
);
const tableHeading = ["Name", "Role","Email", "Status", "Action"];

// Records created before account deactivation existed have no isActive
// field. Absent means active — matching how the server reads it.
const isUserActive = (user) => user?.isActive !== false;

let deleteItemId;
let limit = 10;
let pageCount = 0;

// Convert camelCase to display text as fallback for roles not in the DB
const toDisplayText = (role) => {
  return role.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
};

export default function UsersList({ setItemData, toggleList,setCurrentPage,currentPage }) {
  const dispatch = useDispatch();
  const { getUserListRes, deleteUserRes, sendResetEmailRes, setUserStatusRes, loading } = useSelector((state) => state.staff);
  const currentUserId = useSelector((state) => state.auth.userData?.id);
  const { roles } = useSelector((state) => state.permissions);
  let currentBranch = useSelector(state => state.branch.currentBranch);

  // Build role display map from API data
  const userRole = {};
  if (roles && roles.length > 0) {
    for (const r of roles) { userRole[r.role] = r.displayText; }
  }
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resetEmailModal, setResetEmailModal] = useState({ open: false, userId: null, userName: '' });
  const [statusModal, setStatusModal] = useState({ open: false, userId: null, userName: '', deactivate: true });
  const [statusReason, setStatusReason] = useState('');

  useEffect(() => {
    if (!roles || roles.length === 0) {
      dispatch(getRolesRequest());
    }
  }, [dispatch, roles]);

  useEffect(() => {
    if (!currentBranch) { return }
    dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
  }, [dispatch,currentBranch, currentPage, searchQuery]);

  useEffect(() => {
    if (currentBranch?.id) {
      dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
    }
  }, [dispatch, currentBranch?.id,currentBranch, currentPage, searchQuery]);

  useEffect(() => {
    if (!deleteUserRes) { return }
    if (deleteUserRes?.success) {
      dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
    }
    toast(deleteUserRes?.message);
    dispatch(resetUserDelete());
  }, [dispatch, deleteUserRes,searchQuery, currentPage]);

  // Handle send reset email response
  useEffect(() => {
    if (!sendResetEmailRes) return;
    if (sendResetEmailRes?.success) {
      toast.success('Password reset email sent successfully.');
    } else {
      toast.error(sendResetEmailRes?.message || 'Failed to send reset email. Please try again.');
    }
    dispatch(clearSendResetEmail());
    setResetEmailModal({ open: false, userId: null, userName: '' });
  }, [sendResetEmailRes, dispatch]);

  const closeStatusModal = () => {
    setStatusModal({ open: false, userId: null, userName: '', deactivate: true });
    setStatusReason('');
  };

  // Handle activate/deactivate response
  useEffect(() => {
    if (!setUserStatusRes) return;
    if (setUserStatusRes?.success) {
      toast.success(setUserStatusRes?.message || 'Account status updated.');
      dispatch(getRequestUserList({ page: currentPage, limit, searchQuery }));
    } else {
      toast.error(setUserStatusRes?.message || 'Could not update account status.');
    }
    dispatch(clearSetUserStatus());
    setStatusModal({ open: false, userId: null, userName: '', deactivate: true });
    setStatusReason('');
  }, [setUserStatusRes, dispatch, currentPage, searchQuery]);

  const handleToggleStatus = (user) => {
    setStatusReason('');
    setStatusModal({
      open: true,
      userId: user._id,
      userName: user.name || user.email,
      deactivate: isUserActive(user)
    });
  };

  const confirmToggleStatus = () => {
    dispatch(setUserStatusRequest({
      id: statusModal.userId,
      isActive: !statusModal.deactivate,
      reason: statusModal.deactivate ? statusReason : undefined
    }));
  };

  const handleSendResetEmail = (user) => {
    setResetEmailModal({
      open: true,
      userId: user._id,
      userName: user.name
    });
  };

  const confirmSendResetEmail = () => {
    dispatch(sendResetEmailRequest(resetEmailModal.userId));
  };

  if (getUserListRes?.dataCount && getUserListRes?.dataCount > limit) {
    pageCount = getUserListRes?.dataCount / limit;
    if (pageCount % 2 !== 0) {
        pageCount = Math.ceil(pageCount)
    }
}
  return (
    <>
     <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setCurrentPage(1); }} />
      </Stack>
      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <TableContainer component={Paper}  className='tableShadow'>
        <Table sx={{ minWidth: { xs: 500, md: 700 } }} aria-label="customized table" className='commonTable'>
          <TableHead>
            <TableRow>
              {tableHeading.map(elem => (<StyledTableCell key={elem}>{elem}</StyledTableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getUserListRes?.data?.length > 0 ?
              getUserListRes?.data?.map(elem => {
                const active = isUserActive(elem);
                const isSelf = currentUserId && elem._id === currentUserId;
                return (
                <StyledTableRow key={elem._id} sx={active ? undefined : { opacity: 0.6 }}>
                  <StyledTableCell align="left"><EllipsisText>{elem.name}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left"><EllipsisText>{userRole[elem.role] || toDisplayText(elem.role)}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left"><EllipsisText>{elem.email}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left">
                    <Chip
                      size="small"
                      label={active ? 'Active' : 'Deactivated'}
                      color={active ? 'success' : 'default'}
                      variant={active ? 'outlined' : 'filled'}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <EllipsisText>
                      <Tooltip title="Edit User">
                        <IconButton color="primary" onClick={() => { setItemData(elem); toggleList() }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {active &&
                        <Tooltip title="Send Password Reset Email">
                          <IconButton color="info" onClick={() => handleSendResetEmail(elem)} disabled={loading}>
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      }
                      {/* Self-service status changes are rejected server-side —
                          don't offer the button at all. */}
                      {!isSelf &&
                        <Tooltip title={active ? 'Deactivate User' : 'Reactivate User'}>
                          <IconButton
                            color={active ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(elem)}
                            disabled={loading}
                          >
                            {active ? <BlockIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                      }
                      {getUserListRes?.data?.length > 1 && !isSelf &&
                        <Tooltip title="Delete User">
                          <IconButton color="error" onClick={() => { deleteItemId = elem._id; setIsModalOpen(true) }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    </EllipsisText>
                  </StyledTableCell>
                </StyledTableRow>
                );
              })
              :
              <StyledTableRow>
                <StyledTableCell colSpan={tableHeading.length} align="left"><Typography>There is no data to show</Typography></StyledTableCell>
              </StyledTableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
      {(getUserListRes?.dataCount > limit) && <Pagination count={pageCount} page={currentPage} onChange={(event, value) => { setCurrentPage(value)}} />}
      <DeleteConfirmationModal open={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={() => { dispatch(deleteUserRequest(deleteItemId));  setIsModalOpen(false) }} />

      {/* Activate / Deactivate Confirmation Dialog */}
      <Dialog open={statusModal.open} onClose={closeStatusModal} fullWidth maxWidth="xs">
        <DialogTitle>{statusModal.deactivate ? 'Deactivate User' : 'Reactivate User'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusModal.deactivate
              ? <>Deactivate <strong>{statusModal.userName}</strong>? They will be signed out immediately and cannot log back in. Their records and past work are kept, and you can reactivate them at any time.</>
              : <>Reactivate <strong>{statusModal.userName}</strong>? They will be able to sign in again with their existing password.</>}
          </DialogContentText>
          {statusModal.deactivate &&
            <TextField
              fullWidth
              margin="normal"
              size="small"
              label="Reason (optional)"
              placeholder="e.g. Left the practice"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText="Recorded in the audit log."
            />
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusModal}>Cancel</Button>
          <Button
            onClick={confirmToggleStatus}
            variant="contained"
            color={statusModal.deactivate ? 'warning' : 'success'}
            disabled={loading}
          >
            {loading ? 'Saving...' : (statusModal.deactivate ? 'Deactivate' : 'Reactivate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Email Confirmation Dialog */}
      <Dialog open={resetEmailModal.open} onClose={() => setResetEmailModal({ open: false, userId: null, userName: '' })}>
        <DialogTitle>Send Password Reset Email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to send a password reset email to <strong>{resetEmailModal.userName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetEmailModal({ open: false, userId: null, userName: '' })}>
            Cancel
          </Button>
          <Button onClick={confirmSendResetEmail} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}